/**
 * Accordion — Base UI Accordion primitive (multiple Collapsibles).
 *
 * Defaults to `openMultiple={false}` (single open at a time, with collapsible).
 *
 * Composition:
 *   <Accordion.Root openMultiple={false}>
 *     <Accordion.Item value="one">
 *       <Accordion.Header>
 *         <Accordion.Trigger>Section one</Accordion.Trigger>
 *       </Accordion.Header>
 *       <Accordion.Panel>Body one</Accordion.Panel>
 *     </Accordion.Item>
 *     <Accordion.Item value="two">...</Accordion.Item>
 *   </Accordion.Root>
 */
import * as React from "react"
import { Accordion as BaseAccordion } from "@base-ui-components/react/accordion"
import { cn } from "@lib/cn"

const Root = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseAccordion.Root>
>(({ className, ...props }, ref) => (
  <BaseAccordion.Root
    ref={ref}
    className={cn(
      "rounded-md border border-webex-gray-300 dark:border-webex-gray-700 bg-white dark:bg-webex-gray-950 divide-y divide-webex-gray-200 dark:divide-webex-gray-800",
      className,
    )}
    {...props}
  />
))
Root.displayName = "Accordion.Root"

const Item = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseAccordion.Item>
>(({ className, ...props }, ref) => (
  <BaseAccordion.Item ref={ref} className={cn("", className)} {...props} />
))
Item.displayName = "Accordion.Item"

const Header = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof BaseAccordion.Header>
>(({ className, ...props }, ref) => (
  <BaseAccordion.Header ref={ref} className={cn("m-0", className)} {...props} />
))
Header.displayName = "Accordion.Header"

const Trigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof BaseAccordion.Trigger>
>(({ className, children, ...props }, ref) => (
  <BaseAccordion.Trigger
    ref={ref as React.Ref<HTMLElement>}
    className={cn(
      "flex w-full items-center justify-between gap-2 px-4 py-3",
      "text-sm font-medium text-webex-gray-1000 dark:text-white text-left",
      "cursor-pointer select-none bg-transparent border-0",
      "transition-colors duration-150 motion-reduce:transition-none",
      "hover:bg-webex-gray-50 dark:hover:bg-webex-gray-900",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-[-2px] focus-visible:ring-inset",
      "[&[data-panel-open]_svg]:rotate-180",
      className,
    )}
    {...props}
  >
    <span className="flex-1">{children}</span>
    <svg
      viewBox="0 0 14 14"
      className="size-3.5 shrink-0 text-webex-gray-600 dark:text-webex-gray-400 transition-transform duration-150 motion-reduce:transition-none"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3,5.5 7,9.5 11,5.5" />
    </svg>
  </BaseAccordion.Trigger>
))
Trigger.displayName = "Accordion.Trigger"

const Panel = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseAccordion.Panel>
>(({ className, children, ...props }, ref) => (
  <BaseAccordion.Panel
    ref={ref}
    className={cn(
      "overflow-hidden",
      "h-[var(--accordion-panel-height)]",
      "data-[starting-style]:h-0 data-[ending-style]:h-0",
      "transition-[height] duration-200 motion-reduce:transition-none",
      className,
    )}
    {...props}
  >
    <div className="px-4 pb-4 text-sm text-webex-gray-1000 dark:text-white">{children}</div>
  </BaseAccordion.Panel>
))
Panel.displayName = "Accordion.Panel"

export const Accordion = { Root, Item, Header, Trigger, Panel }
